// src/onus_components/OpticalPowerModal.jsx — la potencia óptica de una ONT
//
// Consulta: se lanza sola al abrir y no cambia nada en la ONT. Lo que
// importa es la lectura, así que va arriba; lo que se le preguntó a la OLT
// queda plegado debajo.

import React, { useCallback, useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";
import { Iconos } from "../componentes/base";
import ModalPasos, { fichaOnu, leerRespuesta, ubicacionDe } from "../componentes/olt/ModalPasos";

/* El backend califica cada lectura como good / warn / bad. */
const TONO = { good: "bien", warn: "aviso", bad: "mal" };
const valor = (v, unidad) => (v == null || v === "" ? "—" : `${v} ${unidad}`);

export default function OpticalPowerModal({ open, onu, onClose }) {
  const [estado, setEstado] = useState("reposo");
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");

  const u = ubicacionDe(onu);

  const consultar = useCallback(async () => {
    if (!onu?.id) return;
    setEstado("corriendo"); setDatos(null); setError("");
    try {
      const res = await authFetch(`/api/onus/${onu.id}/optical_power`);
      const data = await leerRespuesta(res);
      if (!res.ok || !data?.ok) throw new Error(data?.mensaje || "No se pudo leer la potencia.");
      setDatos(data);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setEstado("fin");
    }
  }, [onu?.id]);

  useEffect(() => { if (open) consultar(); }, [open, consultar]);

  const o = datos?.optical || {};
  const info = datos?.onu || onu || {};
  const lectura = [
    ["Recibe la ONT", valor(o.rx_power, "dBm"), TONO[o.rx_status]],
    ["Recibe la OLT", valor(o.olt_rx_ont_power, "dBm"), TONO[o.olt_rx_status]],
    ["Transmite", valor(o.tx_power, "dBm")],
    ["Temperatura", valor(o.temperature, "°C")],
    ["Voltaje", valor(o.voltage, "V")],
    ["Corriente", valor(o.current, "mA")],
  ];
  const malas = [o.rx_status, o.olt_rx_status].filter((s) => s === "bad").length;
  const flojas = [o.rx_status, o.olt_rx_status].filter((s) => s === "warn").length;

  return (
    <ModalPasos
      abierto={open}
      titulo="Potencia óptica"
      icono={Iconos.potencia}
      ficha={fichaOnu({ ...onu, ...info })}
      consulta
      estado={estado}
      error={error}
      textoEspera="Leyendo la óptica en la OLT"
      mensaje={malas ? "La potencia está fuera de margen: revisa la fibra y los conectores."
        : flojas ? "Lectura justa: funciona, pero está cerca del límite."
          : "Lectura buena: dentro del margen."}
      resultado={datos && (
        <>
          <dl className="mp-res">
            {lectura.map(([k, v, t]) => (
              <div key={k} className={t ? `mp-res--${t}` : undefined}><dt>{k}</dt><dd>{v}</dd></div>
            ))}
          </dl>
          <p className="mp-nota" style={{ marginTop: 10 }}>
            Modelo {info.modelo || "—"} · service-port {info.service_port ?? "—"}
          </p>
        </>
      )}
      fases={[
        { rotulo: "Leer la óptica en la OLT", app: true, detalle: `display ont optical-info ${u.pon} ${u.ont}` },
      ]}
      onEjecutar={consultar}
      onCerrar={onClose}
    />
  );
}
